import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Skeleton from '../Skeleton';
import InfoBubble from '../InfoBubble';
import infoIcon from '../../../styles/images/info_circle_icon.svg';
import styles from './RadioButtonGroup.module.scss';

const RadioButtonGroup = ({
  label,
  options = [],
  value,
  onChange,
  name,
  required = false,
  disabled = false,
  error = false,
  errorMessage = '',
  helperText = '',
  showInfoIcon = false,
  onInfoClick,
  infoText,
  isLoading = false,
  className = '',
  ...rest
}) => {
  const [isInfoVisible, setIsInfoVisible] = useState(false);
  const infoBubbleId = `${name}-info`;

  const handleChange = (optionValue) => {
    if (!disabled && onChange) {
      onChange(optionValue, name);
    }
  };

  const handleKeyDown = (e, optionValue, optionDisabled) => {
    if ((e.key === 'Enter' || e.key === ' ') && !optionDisabled) {
      e.preventDefault();
      handleChange(optionValue);
    }
  };

  return (
    <div className={`${styles.radioGroupWrapper} ${className}`} {...rest}>
      {label && (
        <div className={styles.labelContainer}>
          <label className={styles.label}>
            {label}
            {required && <span className={styles.required}>*</span>}
          </label>
          {showInfoIcon && (
            <div
              className={styles.infoIconWrapper}
              onMouseEnter={() => setIsInfoVisible(true)}
              onMouseLeave={() => setIsInfoVisible(false)}
            >
              <button
                type="button"
                className={styles.infoIcon}
                onClick={onInfoClick}
                onFocus={() => setIsInfoVisible(true)}
                onBlur={() => setIsInfoVisible(false)}
                aria-label="More information"
                aria-describedby={infoText && isInfoVisible ? infoBubbleId : undefined}
              >
                <img src={infoIcon} alt="" width="17" height="17" />
              </button>
              {infoText && isInfoVisible && (
                <InfoBubble id={infoBubbleId} className={styles.infoBubble}>
                  {infoText}
                </InfoBubble>
              )}
            </div>
          )}
        </div>
      )}
      <div className={styles.radioGroup} role="radiogroup" aria-labelledby={label ? `${name}-label` : undefined}>
        {isLoading ? (
          <Skeleton height="40px" borderRadius="8px" />
        ) : (
          options.map((option) => {
            const isChecked = option.value === value;
            const isDisabled = disabled || option.disabled;
            const optionId = `${name}-${option.value}`;

            return (
              <label
                key={option.value}
                htmlFor={optionId}
                className={`${styles.radioOption} ${isDisabled ? styles.disabled : ''}`}
              >
                <input
                  id={optionId}
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={isChecked}
                  onChange={() => handleChange(option.value)}
                  onKeyDown={(e) => handleKeyDown(e, option.value, isDisabled)}
                  disabled={isDisabled}
                  className={styles.radioInput}
                  aria-checked={isChecked}
                />
                <span className={styles.radioControl} />
                <span className={styles.radioLabel}>{option.label}</span>
              </label>
            );
          })
        )}
      </div>
      {error && errorMessage && (
        <div id={`${name}-error`} className={styles.errorMessage}>
          {errorMessage}
        </div>
      )}
      {!error && helperText && (
        <div id={`${name}-helper`} className={styles.helperText}>
          {helperText}
        </div>
      )}
    </div>
  );
};

RadioButtonGroup.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  helperText: PropTypes.string,
  showInfoIcon: PropTypes.bool,
  onInfoClick: PropTypes.func,
  infoText: PropTypes.string,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
};

export default RadioButtonGroup;
